import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useLangPrefix from "../hooks/useLangPrefix";
import useRevealSection from "../hooks/useRevealSection";

const TILE_COLS = 10;
const TILE_ROWS = 7;
const TILE_COUNT = TILE_COLS * TILE_ROWS;

const shuffledDelays = (count: number) => {
  const delays = Array.from({ length: count }, (_, i) => i * 18);
  for (let i = delays.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [delays[i], delays[j]] = [delays[j], delays[i]];
  }
  return delays;
};

const TYPING_SPEED_MS = 55;
const NAME_TEXT = "Thomas Grimmenstein";

type TypingPhase = "greeting" | "name" | "role" | "done";

const Home: React.FC = () => {
  const { t } = useTranslation();
  const prefix = useLangPrefix();
  const sectionRef = useRevealSection<HTMLElement>();
  const [replayKey, setReplayKey] = useState(0);
  // Bei jedem Replay (replayKey) neu mischen, damit die Kacheln anders einlaufen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tileDelays = useMemo(() => shuffledDelays(TILE_COUNT), [replayKey]);
  const greetingText = t("home.greeting");
  const roleText = t("home.role");
  const [typedGreeting, setTypedGreeting] = useState("");
  const [typedName, setTypedName] = useState("");
  const [typedRole, setTypedRole] = useState("");
  const [typingPhase, setTypingPhase] = useState<TypingPhase>("greeting");

  useEffect(() => {
    let cancelled = false;
    setTypedGreeting("");
    setTypedName("");
    setTypedRole("");
    setTypingPhase("greeting");

    const typeText = (text: string, setter: (value: string) => void) =>
      new Promise<void>((resolve) => {
        let i = 0;
        const id = setInterval(() => {
          if (cancelled) {
            clearInterval(id);
            resolve();
            return;
          }
          i++;
          setter(text.slice(0, i));
          if (i >= text.length) {
            clearInterval(id);
            resolve();
          }
        }, TYPING_SPEED_MS);
      });

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    (async () => {
      await typeText(greetingText, setTypedGreeting);
      if (cancelled) return;
      setTypingPhase("name");
      await wait(150);
      await typeText(NAME_TEXT, setTypedName);
      if (cancelled) return;
      setTypingPhase("role");
      await wait(300);
      await typeText(roleText, setTypedRole);
      if (cancelled) return;
      setTypingPhase("done");
    })();

    return () => {
      cancelled = true;
    };
  }, [greetingText, roleText, replayKey]);

  useEffect(() => {
    const el = sectionRef.current;
    if (
      !el ||
      typeof window === "undefined" ||
      !("IntersectionObserver" in window)
    ) {
      return;
    }

    let hasLeft = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (hasLeft) {
            setReplayKey((k) => k + 1);
            hasLeft = false;
          }
        } else {
          hasLeft = true;
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sectionRef]);

  return (
    <section className="home" id="home" ref={sectionRef}>
      <div className="home-bg-wrap" aria-hidden="true">
        <div className="home-bg"></div>
        <div className="home-bg-tiles" key={replayKey}>
          {tileDelays.map((delay, i) => (
            <span
              key={i}
              className="home-bg-tile"
              style={{ animationDelay: `${delay}ms` }}
            />
          ))}
        </div>
      </div>
      <div className="home-content">
        <h1 className="reveal">
          {typedGreeting}
          {typingPhase === "greeting" && (
            <span className="typing-cursor" aria-hidden="true"></span>
          )}
          <br />
          <span>
            {typedName}
            {typingPhase === "name" && (
              <span className="typing-cursor" aria-hidden="true"></span>
            )}
          </span>
        </h1>
        <div className="text-animate reveal reveal-d1">
          <h3>
            {typedRole}
            {(typingPhase === "role" || typingPhase === "done") && (
              <span className="typing-cursor" aria-hidden="true"></span>
            )}
          </h3>
        </div>
        <p className="reveal reveal-d2">
          {t("home.introPre")}
          <a href="https://promptdoo.com" target="_blank" rel="noreferrer">
            promptdoo.com
          </a>
          {t("home.introPost")}
        </p>
        <div className="btn-box btn-box-wide reveal reveal-d3">
          <a href={`${prefix}/#projects`} className="btn btn-wide">
            {t("home.cta")}
          </a>
        </div>
      </div>

      <div className="home-sci reveal reveal-d4">
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.xing.com/profile/Thomas_Grimmenstein"
        >
          <i className="bx bxl-xing"></i>
        </a>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.linkedin.com/in/thomas-grimmenstein-779bb4321/"
        >
          <i className="bx bxl-linkedin"></i>
        </a>
      </div>
      <div className="home-imgHover"></div>
    </section>
  );
};

export default Home;
